import { PropsWithChildren, ReactNode, useCallback, useEffect, useState } from 'react'
import { DropdownTrigger } from '../DropdownTrigger/DropdownTrigger'
import Styles from './EditableName.module.scss'
import { Input } from 'almanac-ui/TextInputField/Input'
import { observer } from 'mobx-react-lite'
import { ButtonProps } from 'components/Button/Types'
import { Pressable } from '@react-aria/interactions'
import cx from 'classnames'
import { Placement } from '@floating-ui/react-dom'

export interface Props extends Pick<ButtonProps, 'size'> {
  name: string
  onChange?: (change: string) => void
  onClose?: (updatedName: string) => void
  onOpen?: () => void
  forceOpen?: boolean
  autoFocus?: boolean
  isDisabled?: boolean
  trigger?: ReactNode
  placement?: Placement
}

interface PopoverProps extends Props {
  close: () => void
}

const Popover = ({ close, name, onChange, onOpen, autoFocus }: PopoverProps) => {
  const ref = useCallback(
    (node: HTMLInputElement | null) => {
      if (node && autoFocus) {
        setTimeout(() => node.focus(), 10)
      }
    },
    [autoFocus],
  )

  useEffect(() => {
    onOpen?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.nameInput}>
        <Input
          ref={ref}
          autoFocus={autoFocus}
          aria-label="Name"
          name="name"
          placeholder="Untitled"
          onKeyPress={(event) => {
            if (event.key === 'Enter') close()
          }}
          onChange={(event) => onChange?.(event.currentTarget.value)}
          value={name}
          size="lg"
          className={Styles.input}
        />
      </div>
    </div>
  )
}

export const EditableName = observer(
  ({
    name,
    onChange,
    onClose,
    onOpen,
    trigger,
    autoFocus = false,
    forceOpen,
    isDisabled = false,
    placement = 'bottom-start',
  }: PropsWithChildren<Props>) => {
    const [updatedName, setUpdatedName] = useState(name)

    const handleClose = (isOpen: boolean) => {
      if (!isOpen) {
        onClose?.(updatedName)
      }
    }

    const handleChange = (change: string) => {
      onChange?.(change)
      setUpdatedName(change)
    }

    return (
      <DropdownTrigger
        restoreFocus={false}
        forceOpen={forceOpen}
        placement={placement}
        containFocus={false}
        popoverClassName={Styles.popover}
        autoUpdateOptions={{
          animationFrame: false,
          elementResize: false,
        }}
        onOpenChange={handleClose}
      >
        {trigger || (
          <Pressable isDisabled={isDisabled}>
            <div role="button" tabIndex={0} className={cx(Styles.nameButton, { [Styles.disabled]: isDisabled })}>
              {name}
            </div>
          </Pressable>
        )}
        {(close) => (
          <Popover close={close} name={updatedName} onChange={handleChange} onOpen={onOpen} autoFocus={autoFocus} />
        )}
      </DropdownTrigger>
    )
  },
)
